import { isDepositOperation, } from '../client.js';
import { directPurchaseIntentClient } from '../direct-purchase/intent-client.js';
import { isHostedFundingMethod, } from '../discovery/policy.js';
import { operationSettlement } from '../discovery/settlement-asset.js';
import { createCardMountRecovery } from './card/card-mount-recovery.js';
import { hostedEligibilityRecovery } from './hosted/eligibility.js';
import { hostedResumeEvent } from './hosted/recovery.js';
import { fundingQuoteAmounts, reviewQuote, } from './quote-review.js';
import { sameFundingQuoteTerms } from './quote-terms.js';
import { renewPaymentQuote } from './renew-payment-quote.js';
/** Editing an intent never releases another executable attempt. */
export function createCheckoutIntentActions(context) {
    const { client: fundingClient, progress, navigation, method, attemptId, operation, discovery, state, preview, canContinueAmount, quoteContinuation, pendingCreate, options, currency, decimals, surface, setOperation, setSelected, setQuote, setReview, approveQuote, setPreviewUnits, quoteOperation, run, paymentClosed, paymentSignature, quote, review, setError, setPaymentSignature, setAttemptId, } = context;
    const client = options.directPurchase
        ? directPurchaseIntentClient(fundingClient, options.provider)
        : fundingClient;
    if (operation && !isDepositOperation(operation))
        throw new Error('onramp_direction_mismatch');
    async function recoverOperation(signal) {
        const current = operation ??
            (pendingCreate.current ? await client.createOperation(pendingCreate.current, signal) : null);
        if (current && !isDepositOperation(current))
            throw new Error('onramp_direction_mismatch');
        if (current && !signal.aborted) {
            setOperation(current);
            navigation.trackOperation(current.id);
        }
        return current;
    }
    const quoteIntent = (current) => {
        if (!isDepositOperation(current))
            throw new Error('onramp_direction_mismatch');
        return {
            ...current,
            ...(context.providerPaymentMethodId
                ? { providerPaymentMethodId: context.providerPaymentMethodId }
                : {}),
            settlement: operationSettlement(current),
        };
    };
    function assertPurchaseBinding(current) {
        const requested = options.directPurchase?.binding;
        if (requested &&
            (!current.purchase ||
                current.purchase.marketAddress !== requested.marketAddress ||
                current.purchase.outcomeIndex !== requested.outcomeIndex ||
                current.purchase.mint !== requested.mint ||
                current.purchase.network !== requested.network ||
                current.purchase.caip19 !== requested.caip19))
            throw new Error('onramp_purchase_identity_mismatch');
    }
    async function hostedAvailable(current, signal) {
        const eligibility = await discovery.refetch();
        if (signal.aborted)
            return false;
        if (eligibility.error)
            throw eligibility.error;
        if (eligibility.data) {
            const recovery = hostedEligibilityRecovery(eligibility.data, current.method, options.authenticationRequested);
            if (recovery) {
                progress.method = current.method;
                progress.amountUnits = current.receiveTarget?.localUnits ?? current.principalUnits;
                progress.amountCurrency = current.fiatCurrency;
                progress.amountDecimals = current.fiatDecimals;
                progress.verificationRequested = true;
                setSelected(current.method);
                setQuote(null);
                setReview(null);
                navigation.transition(recovery);
                return false;
            }
        }
        if (!eligibility.data?.methods.includes(current.method)) {
            setError('method_unavailable');
            return false;
        }
        return true;
    }
    async function prepareQuote(current, executableQuote, signal) {
        assertPurchaseBinding(current);
        const checked = reviewQuote(executableQuote, quoteIntent(current), Date.now());
        if (checked.state !== 'ready')
            throw new Error('quote_expired');
        const attempt = await client.prepareAttempt(current.id, executableQuote.signature, executableQuote.expiresAt, options.sessionProofId, signal, fundingQuoteAmounts(checked), context.providerPaymentMethodId);
        if (signal.aborted)
            return;
        setAttemptId(attempt.attemptId);
        if (attempt.executable) {
            paymentClosed.current = false;
            if (current.method === 'card' || current.method === 'sepa') {
                // Buy starts on mount. Persist the uncertainty fence before releasing its signature.
                navigation.markAttempt();
                const resume = createCardMountRecovery({
                    client,
                    operation: { ...quoteIntent(current), id: current.id },
                    attemptId: attempt.attemptId,
                    sessionProofId: options.sessionProofId,
                    quote: executableQuote,
                    reviewFreshQuote: (retrySignal, onReady) => quoteOperation(current, retrySignal, onReady),
                    review: () => {
                        navigation.transition('quote_ready');
                    },
                    execute: (signature) => {
                        context.pendingCardMount.current = null;
                        approveQuote();
                        setPaymentSignature(signature);
                        navigation.transition('attempt_executable');
                    },
                });
                context.pendingCardMount.current = resume;
                await resume(signal, true);
                if (signal.aborted)
                    return;
                return;
            }
            approveQuote();
            setPaymentSignature(executableQuote.signature);
        }
        navigation.transition(attempt.executable ? 'attempt_executable' : 'attempt_already_exists');
    }
    return {
        changeMethod: () => {
            if (attemptId)
                return;
            void run(async (signal) => {
                const current = await recoverOperation(signal);
                if (signal.aborted)
                    return;
                if (current)
                    await client.cancelOperation(current.id, signal);
                if (signal.aborted)
                    return;
                setOperation(null);
                navigation.trackOperation(null);
                setSelected(null);
                progress.method = null;
                if (!options.directPurchase)
                    progress.amountUnits = null;
                progress.verificationRequested = false;
                setQuote(null);
                setReview(null);
                pendingCreate.current = null;
                navigation.transition('change_method');
            });
        },
        quoteAmount: (units, paySelectedCard = false) => run(async (signal) => {
            if (options.directPurchase && options.directPurchaseEnabled !== true)
                return;
            if (operation)
                assertPurchaseBinding(operation);
            if (!method)
                return;
            if (attemptId && operation && isHostedFundingMethod(method)) {
                if (paymentClosed.current || !paymentSignature) {
                    navigation.transition('payment_uncertain');
                    return;
                }
                const event = await hostedResumeEvent(client, operation.id, signal);
                if (event)
                    navigation.transition(event);
                return;
            }
            if ((options.directPurchase || method !== 'card') &&
                (discovery.data?.connectionRequiredMethods?.includes(method) ||
                    (discovery.data?.status === 'connectionRequired' &&
                        !((method === 'apple_pay' || method === 'google_pay') && discovery.data.guest)) ||
                    preview.providerFailure?.category === 'connection_required')) {
                if (!/^[1-9]\d{0,19}$/.test(units) || !canContinueAmount)
                    return;
                progress.amountUnits = units;
                progress.amountCurrency = currency;
                progress.amountDecimals = decimals;
                setPreviewUnits(units);
                navigation.transition(discovery.data?.authenticationReason === 'account_mismatch'
                    ? 'connection_required'
                    : 'connect_requested');
                return;
            }
            if (method === 'card' && (!/^[1-9]\d{0,19}$/.test(units) || !canContinueAmount))
                return;
            if (method !== 'card' && discovery.data?.needsContactVerification) {
                if (!/^[1-9]\d{0,19}$/.test(units))
                    return;
                progress.amountUnits = units;
                progress.amountCurrency = currency;
                progress.amountDecimals = decimals;
                progress.verificationRequested = true;
                setPreviewUnits(units);
                navigation.transition('contacts_required');
                return;
            }
            // The amount preview is inactive on review; refresh the existing intent directly.
            if (state === 'review' &&
                operation !== null &&
                method !== 'card' &&
                !attemptId &&
                (operation?.receiveTarget?.localUnits ?? operation?.principalUnits) === units &&
                operation.method === method &&
                ['created', 'action_required'].includes(operation.status)) {
                await quoteOperation(operation, signal);
                return;
            }
            const accepted = preview.state === 'ready' &&
                preview.amountUnits === units &&
                preview.quote &&
                Date.parse(preview.quote.expiresAt) > Date.now()
                ? preview.quote
                : undefined;
            if (!attemptId &&
                method !== 'card' &&
                !discovery.data?.needsContactVerification &&
                (!canContinueAmount || !accepted))
                return;
            if (attemptId) {
                if (!operation || !quoteContinuation.current)
                    return;
                if ((operation.receiveTarget?.localUnits ?? operation.principalUnits) === units) {
                    const updateQuote = quoteContinuation.current;
                    // A cancelled edit may already have changed the server's target.
                    await quoteOperation(operation, signal, async (ready) => {
                        await renewPaymentQuote(client, { ...quoteIntent(operation), id: operation.id }, attemptId, options.sessionProofId, ready, updateQuote, signal, units);
                        if (signal.aborted)
                            return;
                        approveQuote();
                        setPaymentSignature(ready.signature);
                        navigation.transition('attempt_executable');
                    });
                    return;
                }
                if (!canContinueAmount || !accepted || preview.review?.state !== 'ready')
                    return;
                const revised = {
                    ...operation,
                    principalUnits: preview.review.estimate.principalUnits,
                    ...(operation.receiveTarget
                        ? { receiveTarget: { ...operation.receiveTarget, localUnits: units } }
                        : {}),
                    quotedAmounts: fundingQuoteAmounts(preview.review),
                };
                await renewPaymentQuote(client, { ...quoteIntent(revised), id: revised.id }, attemptId, options.sessionProofId, accepted, quoteContinuation.current, signal, units);
                if (signal.aborted)
                    return;
                setOperation(revised);
                setQuote(accepted);
                setReview(preview.review);
                setPaymentSignature(accepted.signature);
                navigation.transition('attempt_executable');
                return;
            }
            let current = await recoverOperation(signal);
            if (signal.aborted)
                return;
            if (current && !['created', 'action_required'].includes(current.status)) {
                navigation.transition('payment_uncertain');
                return;
            }
            if (current &&
                ((current.receiveTarget?.localUnits ?? current.principalUnits) !== units ||
                    current.method !== method)) {
                await client.cancelOperation(current.id, signal);
                if (signal.aborted)
                    return;
                pendingCreate.current = null;
                current = null;
                setOperation(null);
                navigation.trackOperation(null);
            }
            if (!current) {
                // Retain the exact request until an ambiguous create has been recovered.
                pendingCreate.current ??= {
                    idempotencyKey: options.createId(),
                    ...(options.directPurchase
                        ? {
                            purpose: 'direct_token_purchase',
                            purchase: {
                                ...options.directPurchase.purchase,
                                origin: options.directPurchase.origin,
                            },
                        }
                        : {}),
                    method,
                    surface,
                    fiatCurrency: currency,
                    fiatDecimals: decimals,
                    principalUnits: preview.review?.estimate.principalUnits ?? units,
                    ...(!options.directPurchase &&
                        options.fundingFx &&
                        (preview.review?.estimate.targetUnits !== undefined ||
                            (method === 'card' && discovery.data?.context.asset === 'USDC_SOL'))
                        ? { receiveTarget: { localUnits: units, fx: options.fundingFx } }
                        : {}),
                    ...(options.locale ? { locale: options.locale } : {}),
                };
                const created = await client.createOperation(pendingCreate.current, signal);
                if (!isDepositOperation(created))
                    throw new Error('onramp_direction_mismatch');
                current = created;
            }
            if (signal.aborted)
                return;
            setOperation(current);
            navigation.trackOperation(current.id);
            if (current.status !== 'created' && current.status !== 'action_required') {
                navigation.transition('payment_uncertain');
                return;
            }
            if (method === 'card') {
                progress.amountUnits = units;
                progress.amountCurrency = currency;
                progress.amountDecimals = decimals;
                setPreviewUnits(units);
                await context.continueCard(current, signal, paySelectedCard && context.providerPaymentMethodId
                    ? async (ready) => {
                        if (!accepted || !sameFundingQuoteTerms(accepted, ready)) {
                            navigation.transition('quote_ready');
                            return;
                        }
                        await prepareQuote(current, ready, signal);
                    }
                    : undefined);
                return;
            }
            if (discovery.data?.guest) {
                const payer = await client.getPayerStatus(signal);
                if (signal.aborted)
                    return;
                if (!payer.emailVerified ||
                    !payer.phoneVerified ||
                    (payer.consentRequired !== false && !payer.consentAccepted)) {
                    navigation.transition('contacts_required');
                    return;
                }
            }
            if (isHostedFundingMethod(current.method) && !(await hostedAvailable(current, signal)))
                return;
            if (!accepted)
                return;
            const checked = reviewQuote(accepted, quoteIntent(current), Date.now());
            if (checked.state === 'expired')
                throw new Error('quote_expired');
            context.presentQuote(current.id, accepted, checked);
            if (checked.challenge && checked.state !== 'limit_exceeded') {
                context.setChallengeUrl(checked.challenge.url);
                navigation.transition('quote_challenge');
                return;
            }
            if (checked.state !== 'ready') {
                navigation.transition('quote_ready');
                return;
            }
            // Continue consumes the executable quote already displayed; it never requotes implicitly.
            if (current.method === 'sepa')
                navigation.transition('quote_ready');
            else
                await prepareQuote(current, accepted, signal);
        }),
        pay: () => {
            if (options.directPurchase && options.directPurchaseEnabled !== true)
                return Promise.resolve();
            return run(async (signal) => {
                if (operation)
                    assertPurchaseBinding(operation);
                const pendingMount = context.pendingCardMount.current;
                if (pendingMount)
                    return pendingMount(signal, true);
                if (operation && attemptId && isHostedFundingMethod(operation.method)) {
                    if (paymentClosed.current || !paymentSignature) {
                        navigation.transition('payment_uncertain');
                        return;
                    }
                    const event = await hostedResumeEvent(client, operation.id, signal);
                    if (event) {
                        if (event === 'payment_uncertain') {
                            paymentClosed.current = true;
                            setPaymentSignature(null);
                        }
                        navigation.transition(event);
                    }
                    return;
                }
                if (review?.state === 'limit_exceeded')
                    return;
                if (!operation ||
                    !quote ||
                    !review ||
                    reviewQuote(quote, quoteIntent(operation), Date.now()).state !== 'ready') {
                    setError('quote_expired');
                    return;
                }
                if (attemptId) {
                    if (paymentClosed.current)
                        return;
                    const updateQuote = quoteContinuation.current;
                    if (!updateQuote)
                        return;
                    await renewPaymentQuote(client, { ...quoteIntent(operation), id: operation.id }, attemptId, options.sessionProofId, quote, updateQuote, signal);
                    if (signal.aborted)
                        return;
                    approveQuote();
                    setPaymentSignature(quote.signature);
                    navigation.transition('attempt_executable');
                    return;
                }
                await prepareQuote(operation, quote, signal);
            });
        },
        cancelIntent: () => run(async (signal) => {
            if (attemptId)
                return;
            progress.verificationRequested = false;
            const current = await recoverOperation(signal);
            if (signal.aborted)
                return;
            if (!current) {
                navigation.transition('edit_amount');
                return;
            }
            await client.cancelOperation(current.id, signal);
            if (!signal.aborted) {
                setOperation(null);
                navigation.trackOperation(null);
                setQuote(null);
                setReview(null);
                pendingCreate.current = null;
                navigation.transition('edit_amount');
            }
        }),
    };
}
